import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetPostCreationOptions } from '@/features/api/hooks/grapevine-post/useGetPostCreationOptions';
import { PostCreationContent } from '@/features/post-editor/components/PostCreationContent';
import { MINISTRY_GEO_AREA_DEPTH } from '@/features/post-editor/constants/postForm';
import { useGetPostFeedTypeFeatures } from '@/features/post-editor/hooks/useGetPostFeedTypeFeatures';
import { PostCreationContext } from '@/features/post-editor/types/postCreationOptions';
import {
  getAvailableSenderOptions,
  getSenderChannelOptions,
  selectSenderOption
} from '@/features/post-editor/utils/postCreationOptions';
import { SenderChipContainer } from '@/features/sender-selection/components';
import { useLastSenderStore } from '@/features/sender-selection/store/useLastSenderStore';
import type { Sender } from '@/features/sender-selection/types/sender';
import { SenderType } from '@/features/sender-selection/types/sender';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationBar } from '@/navigation/components/navbar/NavigationBar';
import { useNavigationHeader } from '@/navigation/hooks/useNavigationHeader';
import { NavBarType } from '@/navigation/types/navbar';
import type {
  AppNativeStackScreenProps,
  AppParam,
  AppRoute
} from '@/navigation/types/navigation';
import { StateView } from '@/shared/components/state-view/StateView';

type Props = AppNativeStackScreenProps<typeof AppRoute.PostCreationHome>;
const USER_SENDER = { type: SenderType.USER } as const;

type OptionsScreenProps = {
  navigation: NativeStackNavigationProp<AppParam>;
  creationContext: PostCreationContext;
  channelType?: Props['route']['params']['channelType'];
  groupId?: string;
};

export const PostCreationOptionsScreen: FC<OptionsScreenProps> = ({
  navigation,
  creationContext,
  channelType,
  groupId
}) => {
  const { t } = useTranslation(LocalizationNameSpace.POST_CREATION, {
    keyPrefix: 'screens.post-editor'
  });
  const {
    data: options,
    isPending,
    isError
  } = useGetPostCreationOptions(creationContext);
  const isLoadingOptions = isPending && !isError;
  const savedSender = useLastSenderStore((state) => state.sender);
  const setSender = useLastSenderStore((state) => state.setSender);
  const [senderSelectedManually, setSenderSelectedManually] = useState(false);
  const availableSenders = useMemo(
    () => getAvailableSenderOptions(options),
    [options]
  );
  const selectedOption = selectSenderOption(
    availableSenders,
    savedSender,
    channelType,
    senderSelectedManually
  );
  const sender: Sender = useMemo(
    () =>
      selectedOption?.senderType === SenderType.ORGANIZATION &&
      selectedOption.organization?.id
        ? {
            type: SenderType.ORGANIZATION,
            organizationId: selectedOption.organization.id
          }
        : USER_SENDER,
    [selectedOption]
  );
  useEffect(() => {
    if (
      (creationContext !== PostCreationContext.MEIN_ORT &&
        creationContext !== PostCreationContext.MEINE_REGION &&
        creationContext !== PostCreationContext.SAGS_UNS) ||
      isPending ||
      isError ||
      !selectedOption
    ) {
      return;
    }
    if (
      savedSender.type !== sender.type ||
      (savedSender.type === SenderType.ORGANIZATION &&
        sender.type === SenderType.ORGANIZATION &&
        savedSender.organizationId !== sender.organizationId)
    ) {
      setSender(sender);
    }
  }, [
    creationContext,
    isPending,
    isError,
    selectedOption,
    savedSender,
    sender,
    setSender
  ]);
  const senderChannelOptions = selectedOption?.channels ?? [];
  const postFeedTypeFeatures = useGetPostFeedTypeFeatures();
  const senderChannelOptionsForDisplay = getSenderChannelOptions(
    postFeedTypeFeatures,
    senderChannelOptions
  );
  const senderAvailable = senderChannelOptions.length > 0;
  const organizations = useMemo(
    () =>
      availableSenders.flatMap((option) =>
        option.senderType === SenderType.ORGANIZATION && option.organization
          ? [option.organization]
          : []
      ),
    [availableSenders]
  );
  const showPersonalProfile = availableSenders.some(
    (option) => option.senderType === SenderType.USER
  );

  const header = useMemo(
    () => (
      <NavigationBar
        type={NavBarType.MODAL_WITH_CLOSE}
        centerTitle
        direction="row-reverse"
        titleComponent={
          <SenderChipContainer
            sender={sender}
            organizations={organizations}
            disabled={
              availableSenders.length <= 1 ||
              (creationContext !== PostCreationContext.MEIN_ORT &&
                creationContext !== PostCreationContext.MEINE_REGION &&
                creationContext !== PostCreationContext.SAGS_UNS)
            }
            showPersonalProfile={showPersonalProfile}
            onSenderSelect={() => setSenderSelectedManually(true)}
          />
        }
      />
    ),
    [
      sender,
      organizations,
      creationContext,
      availableSenders.length,
      showPersonalProfile
    ]
  );
  useNavigationHeader(navigation, header);

  if (isPending || isError || !senderAvailable) {
    return (
      <StateView
        isLoading={isLoadingOptions}
        isError={isError}
        isEmpty={!senderAvailable}
        stateLabels={t('state', { returnObjects: true })}
        state={{
          empty: <></>
        }}
      />
    );
  }

  return (
    <PostCreationContent
      key={`${creationContext}:${sender.type === SenderType.ORGANIZATION ? sender.organizationId : sender.type}:${senderChannelOptions.map(({ channelType, postTypes }) => `${channelType}:${postTypes.join(',')}`).join(';')}`}
      navigation={navigation}
      sender={sender}
      creationContext={creationContext}
      groupId={groupId}
      channelType={channelType}
      autoSelectOrganizationGeoArea={
        creationContext === PostCreationContext.MEIN_ORT ||
        creationContext === PostCreationContext.SAGS_UNS
      }
      geoAreaDepth={MINISTRY_GEO_AREA_DEPTH}
      senderChannels={senderChannelOptions}
      senderChannelOptions={senderChannelOptionsForDisplay}
    />
  );
};

export const PostCreationHomeScreen: FC<Props> = ({ route, navigation }) => (
  <PostCreationOptionsScreen navigation={navigation} {...route.params} />
);
